/**
 * The balance data every plane, missile and upgrade is flown and drawn with:
 * the ships' handling and collision circles, the missiles' exhaust and
 * thruster sizes, and the upgrade factors. The values are legacy's and match
 * ab-server's own tables, which is what keeps the client's dead reckoning in
 * step with the server between updates.
 */
import { MOB_TYPES } from '@airmash/protocol';
import { PlaneType } from './Constants';

/**
 * A ship's handling, per frame at 60 fps (Input scales the turn by 60 for
 * radians per second), and its collision circles.
 */
export interface ShipConfig {
  /** Radians turned per frame with a turn key held. */
  readonly turnFactor: number;
  /** Speed gained per frame with the throttle held, in world units per frame. */
  readonly accelFactor: number;
  /** The speed cap in world units per frame, before boost, upgrades and the flag. */
  readonly maxSpeed: number;
  /** The speed under which a coasting ship counts as stopped. */
  readonly minSpeed: number;
  /** The fraction of speed lost per frame without throttle. */
  readonly brakeFactor: number;
  /** The energy fraction under which the HUD's energy arc dims. */
  readonly energyLight: number;
  /** Hit circles as [x, y, radius] in ship space. */
  readonly collisions: readonly (readonly [x: number, y: number, radius: number])[];
}

/** A missile's exhaust and thruster look. */
export interface MissileLook {
  /** How far behind the missile's centre the thruster sits, in world units. */
  readonly exhaust: number;
  /** The thruster sprite's scale. */
  readonly thruster: readonly [xScale: number, yScale: number];
}

/** An upgrade line's effect by level, 0 to 5. */
export interface UpgradeConfig {
  readonly factor: readonly number[];
}

/** The four upgrade lines in the order the upgrade packets and the loadout slots use. */
export const UPGRADE_KINDS = ['speed', 'defense', 'energy', 'missile'] as const;

export type UpgradeKind = (typeof UPGRADE_KINDS)[number];

/** Every ship's handling by plane type. */
export const SHIPS: Readonly<Record<PlaneType, ShipConfig>> = {
  [PlaneType.Predator]: {
    turnFactor: 0.065,
    accelFactor: 0.225,
    maxSpeed: 5.5,
    minSpeed: 0.001,
    brakeFactor: 0.025,
    energyLight: 0.6,
    collisions: [
      [0, 5, 23],
      [0, -15, 15],
      [0, -25, 12],
    ],
  },
  [PlaneType.Goliath]: {
    turnFactor: 0.04,
    accelFactor: 0.15,
    maxSpeed: 3.5,
    minSpeed: 0.001,
    brakeFactor: 0.015,
    energyLight: 0.9,
    collisions: [
      [0, 0, 35],
      [50, 14, 16],
      [74, 26, 14],
      [30, 8, 23],
      [63, 22, 15],
      [-50, 14, 16],
      [-74, 26, 14],
      [-30, 8, 23],
      [-63, 22, 15],
    ],
  },
  [PlaneType.Mohawk]: {
    turnFactor: 0.07,
    accelFactor: 0.275,
    maxSpeed: 6,
    minSpeed: 0.001,
    brakeFactor: 0.025,
    energyLight: 0.3,
    collisions: [
      [0, -12, 15],
      [0, 0, 17],
      [0, 13, 15],
      [0, 26, 15],
    ],
  },
  [PlaneType.Tornado]: {
    turnFactor: 0.055,
    accelFactor: 0.2,
    maxSpeed: 4.5,
    minSpeed: 0.001,
    brakeFactor: 0.025,
    energyLight: 0.5,
    collisions: [
      [0, 8, 18],
      [14, 12, 13],
      [-14, 12, 13],
      [0, -12, 16],
      [0, -26, 14],
      [0, -35, 12],
    ],
  },
  [PlaneType.Prowler]: {
    turnFactor: 0.055,
    accelFactor: 0.2,
    maxSpeed: 4.5,
    minSpeed: 0.001,
    brakeFactor: 0.025,
    energyLight: 0.75,
    collisions: [
      [0, 11, 25],
      [0, -8, 18],
      [19, 20, 10],
      [-19, 20, 10],
      [0, -20, 14],
    ],
  },
};

/** The look of every mob type that is a missile; a crate has no entry. */
export const MISSILE_LOOKS: Readonly<Partial<Record<MOB_TYPES, MissileLook>>> = {
  [MOB_TYPES.PREDATOR_MISSILE]: { exhaust: 20, thruster: [0.2, 0.4] },
  [MOB_TYPES.GOLIATH_MISSILE]: { exhaust: 30, thruster: [0.3, 0.6] },
  [MOB_TYPES.COPTER_MISSILE]: { exhaust: 18, thruster: [0.14, 0.3] },
  [MOB_TYPES.TORNADO_MISSILE]: { exhaust: 20, thruster: [0.2, 0.4] },
  [MOB_TYPES.TORNADO_SMALL_MISSILE]: { exhaust: 20, thruster: [0.2, 0.4] },
  [MOB_TYPES.PROWLER_MISSILE]: { exhaust: 20, thruster: [0.2, 0.4] },
};

/** Each upgrade line's factor by level; the server keeps one curve per line, so this does too. */
export const UPGRADES: Readonly<Record<UpgradeKind, UpgradeConfig>> = {
  speed: { factor: [1, 1.05, 1.1, 1.15, 1.2, 1.25] },
  defense: { factor: [1, 1.05, 1.1, 1.15, 1.2, 1.25] },
  energy: { factor: [1, 1.05, 1.1, 1.15, 1.2, 1.25] },
  missile: { factor: [1, 1.05, 1.1, 1.15, 1.2, 1.25] },
};
